import { useCallback, useMemo, useEffect, useState } from 'react';
import ReactFlow, {
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  MarkerType
} from 'reactflow';
import 'reactflow/dist/style.css';

function Flow({ data }) {
  const [workflow, setWorkflow] = useState(data);

  // React Flow state hooks
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  // Update internal workflow when data changes
  useEffect(() => {
    setWorkflow(data);
  }, [data]);

  const statusMap = useMemo(() => (
    Object.fromEntries(workflow.map(status => [status.value, status]))
  ), [workflow]);

  const convertToNodes = useCallback((workflow) => {
    return workflow.map((status, index) => ({
      id: status.value.toString(),
      data: { label: status.label },
      position: {
        x: (index % 4) * 250,
        y: Math.floor(index / 4) * 150
      },
      style: {
        background: status.background || '#fff',
        color: status.color || '#000',
        border: `1px solid ${status.color || '#aaa'}`,
        borderRadius: 8,
        padding: 10
      }
    }));
  }, []);

  const convertToEdges = useCallback((workflow) => {
    const edges = [];
    for (const status of workflow) {
      if (!status.nextStatuses) continue;
      for (const next of status.nextStatuses) {
        if (!statusMap[next.value]) continue;
        edges.push({
          id: `e${status.value}-${next.value}`,
          source: status.value.toString(),
          target: next.value.toString(),
          type: 'smoothstep',
          label: next.label || '',
          labelStyle: { fontSize: 10, fill: '#444' },
          style: { stroke: '#000' },
          markerEnd: {
            type: MarkerType.ArrowClosed,
            width: 20,
            height: 20,
            color: status.color || '#888'
          }
        });
      }
    }
    return edges;
  }, [statusMap]);

  // 🛠 Update nodes and edges whenever workflow changes
  useEffect(() => {
    setNodes(convertToNodes(workflow));
    setEdges(convertToEdges(workflow));
  }, [workflow, convertToNodes, convertToEdges, setNodes, setEdges]);

  const onConnect = useCallback((params) =>
    setEdges((eds) =>
      addEdge(
        {
          ...params,
          type: 'smoothstep',
          markerEnd: { type: MarkerType.ArrowClosed }
        },
        eds
      )
    ), [setEdges]);

  return (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      onNodesChange={onNodesChange}
      onEdgesChange={onEdgesChange}
      onConnect={onConnect}
      fitView
    >
      <MiniMap />
      <Controls />
      <Background gap={12} size={1} />
    </ReactFlow>
  );
}

export default Flow;
